import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ControlledSelect, SelectOption } from '@duke-hq/ui/components/Form/Select';
import { SelectUserField, SelectUserFieldProps } from '@duke-hq/ui/components/Form/SelectUserField';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import {
  getAdminNoteCategoryOptions,
  getAdminNoteOutcomeOptions,
  getAdminNoteTypeOptions,
} from './utils';
import { NoteInterface } from './types';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Controller, FieldPath, FieldValues, useForm } from 'react-hook-form';
import { useDebounce } from 'use-debounce';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { Drn } from '@duke-hq/drn/type';

function CommonSelectUserField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  consultantId,
  ...props
}: Omit<SelectUserFieldProps<TFieldValues, TName>, 'userList' | 'onInputChange'> & {
  consultantId?: string;
}) {
  const { userDrn, providerId } = props;
  const [userInput, setUserInput] = useState<string>();
  const [debouncedSearch] = useDebounce(userInput, 500);

  const userList = trpcAdmin.providers.user.searchUser.useQuery(
    {
      providerId: providerId,
      search: debouncedSearch,
      defaultUserDrn: userDrn,
      defaultConsultantId: consultantId,
    },
    {
      enabled: !!providerId,
    },
  );

  const onInputChange = useCallback((input: string) => {
    setUserInput(input);
  }, []);

  return (
    <SelectUserField
      {...props}
      userList={userList.data || []}
      onInputChange={onInputChange}
      value={userInput}
    />
  );
}

export function NotesComposer({
  userDrn,
  tenantId,
  consultantId,
  refetchNotes,
}: {
  userDrn: Drn;
  tenantId: number;
  consultantId?: string;
  refetchNotes: () => void;
}) {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [noteType, setNoteType] = useState<string | null>(null);
  const toast = useToast();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    resetField,
    clearErrors,
  } = useForm<NoteInterface & { isPinned?: boolean }>({
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
    defaultValues: {
      note_type: undefined,
      with_user_drn: userDrn || null,
      about_user_drn: userDrn || null,
      outcome: null,
      category: null,
      text: '',
      pinnedAt: null,
      isPinned: false,
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const createNote = trpcAdmin.students.createAdminNote.useMutation({
    onSuccess: () => {
      refetchNotes();
      handleClose();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to create note`, description: error.message });
    },
  });

  const noteTypeOptions = getAdminNoteTypeOptions();
  const noteCategoryOptions = noteType != null ? getAdminNoteCategoryOptions(noteType) : [];
  const noteOutcomeOptions = noteType != null ? getAdminNoteOutcomeOptions(noteType) : [];

  const isNoteCategoryRequired = noteType != null && !!noteCategoryOptions?.length;
  const isNoteOutcomeRequired = noteType != null && !!noteOutcomeOptions?.length;

  const onSubmit = (data: NoteInterface & { isPinned?: boolean }) => {
    createNote.mutate({
      ...data,
      date_time: new Date(),
      pinnedAt: data.isPinned ? new Date() : null,
    });
  };

  const handleNoteTypeChange = useCallback(
    (selected: SelectOption) => {
      if (selected != null && 'value' in selected) {
        setNoteType(selected.value);
        clearErrors();
        resetField('category');
        resetField('outcome');
      }
    },
    [clearErrors, resetField],
  );
  return (
    <FormModalWrapper
      isOpen={isOpen}
      onClose={handleClose}
      onOpen={onOpen}
      modalOpenButtonText="Add note"
      modalOpenButtonLeftIcon={Plus}
      modalOpenButtonProps={{ size: 'md', display: 'flex', w: 'fit-content', mx: 'auto', mt: 2 }}
      size="2xl"
      modalContentHeight="auto"
      modalContent={
        <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ width: '100%' }}>
          <FormMainContainer>
            <FormContentContainer>
              <VStack align="stretch" spacing={4} w="full" pb={2}>
                <Text fontSize="lg" fontWeight="semibold">
                  Add a note
                </Text>

                <HStack maxW="50%">
                  <ControlledSelect
                    control={control}
                    options={noteTypeOptions}
                    onChange={handleNoteTypeChange}
                    name="note_type"
                    label="Type"
                    isRequired
                    rules={{ required: 'Select a type' }}
                  />
                </HStack>

                <HStack>
                  <ControlledSelect
                    control={control}
                    options={noteCategoryOptions}
                    name="category"
                    label="Category"
                    isRequired={isNoteCategoryRequired}
                    isDisabled={!isNoteCategoryRequired}
                    rules={{
                      required: isNoteCategoryRequired ? 'Select a category' : false,
                      validate: isNoteCategoryRequired
                        ? (value) => value !== undefined || 'Select a category'
                        : undefined,
                    }}
                  />
                  <ControlledSelect
                    control={control}
                    options={noteOutcomeOptions}
                    name="outcome"
                    label="Outcome"
                    isRequired={isNoteOutcomeRequired}
                    isDisabled={!isNoteOutcomeRequired}
                    rules={{
                      required: isNoteOutcomeRequired ? 'Select an outcome' : false,
                      validate: isNoteOutcomeRequired
                        ? (value) => value !== undefined || 'Select an outcome'
                        : undefined,
                    }}
                  />
                </HStack>

                <HStack>
                  <CommonSelectUserField
                    control={control}
                    name="with_user_drn"
                    label="With"
                    isRequired
                    rules={{ required: 'Select with' }}
                    providerId={tenantId}
                    userDrn={userDrn}
                    consultantId={consultantId}
                  />
                  <CommonSelectUserField
                    control={control}
                    name="about_user_drn"
                    label="About"
                    isRequired
                    rules={{ required: 'Select about' }}
                    providerId={tenantId}
                    userDrn={userDrn}
                    consultantId={consultantId}
                  />
                </HStack>

                <FormControl isRequired isInvalid={errors.text?.message != null}>
                  <FormLabel color="fg.emphasis" fontWeight="normal" fontSize="small" mb={1}>
                    <Trans>Description</Trans>
                  </FormLabel>
                  <Controller
                    rules={{
                      required: 'Description is required',
                    }}
                    control={control}
                    name="text"
                    render={({ field }) => (
                      <Textarea height="auto" rows={5} isRequired {...field} />
                    )}
                  />
                  <FormErrorMessage>{errors.text?.message}</FormErrorMessage>
                </FormControl>

                <Controller
                  control={control}
                  name="isPinned"
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      alignSelf="flex-start"
                      isChecked={value || false}
                      onChange={(e) => onChange(e.target.checked)}
                    >
                      <Trans>Pin note</Trans>
                    </Checkbox>
                  )}
                />
              </VStack>
            </FormContentContainer>

            <FormFooterContainer>
              <Button type="submit" isLoading={createNote.isPending}>
                Create note
              </Button>
            </FormFooterContainer>
          </FormMainContainer>
        </form>
      }
    />
  );
}
